import React, { useState } from 'react';
import { Users, MessageSquare, Calendar, CheckCircle, Clock, Sparkles, ArrowRight, Heart, Edit, Trash2, X, Gift, Check } from 'lucide-react';
import PrimaryButton from './themed/PrimaryButton';
import ThemedCard from './themed/ThemedCard';
import CollaborationRoleBadge from './CollaborationRoleBadge';
import { WeddingInvitation } from '../types/invitation';
import { invitationService } from '../lib/supabase';
import { createLogger } from '../lib/logger';

const log = createLogger('app');

interface RSVPStats {
  total: number;
  attending: number;
  declined: number;
  totalGuests: number;
}

interface DashboardProps {
  setActiveModule: (module: string) => void;
  onQuickInvite?: () => void;
  weddingInvitations?: WeddingInvitation[];
  onEditInvitation?: (inviteCode: string) => void;
  onDeleteInvitation?: (invitationId: string) => void;
  loadingInvitations?: boolean;
  rsvpStats?: RSVPStats;
  weddingDate?: string;
  onRetryLoad?: () => void;
}
export const Dashboard: React.FC<DashboardProps> = ({
  setActiveModule,
  onQuickInvite,
  weddingInvitations = [],
  onEditInvitation,
  onDeleteInvitation,
  loadingInvitations = false,
  onRetryLoad,
  rsvpStats,
  weddingDate
}) => {
  log.debug('接收到 props - loadingInvitations:', loadingInvitations);
  log.debug('接收到 props - weddingInvitations.length:', weddingInvitations.length);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    invitation: WeddingInvitation | null;
  }>({ show: false, invitation: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (invitation: WeddingInvitation) => {
    setDeleteConfirm({ show: true, invitation });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.invitation) return;
    
    try {
      setIsDeleting(true);
      log.debug('正在刪除邀請函:', deleteConfirm.invitation.id);
      
      await invitationService.deleteInvitation(deleteConfirm.invitation.id);
      log.info('邀請函刪除成功，調用父組件回調');
      
      onDeleteInvitation?.(deleteConfirm.invitation.id);
      setDeleteConfirm({ show: false, invitation: null });
      
      log.info('刪除操作完成');
    } catch (error) {
      log.error('刪除邀請函失敗:', error);
      
      // 錯誤處理
      let errorMessage = '刪除失敗，請稍後再試';
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String((error as any).message);
        if (message.includes('row-level security policy')) {
          errorMessage = 'RLS 政策錯誤：請在 Supabase 執行 database/20250622_force-disable-rls.sql';
        } else if (message.includes('401') || message.includes('permission')) {
          errorMessage = '權限錯誤：請參考 documentation/20250622_development-rls-disable-guide.md 解決';
        } else {
          errorMessage = `刪除失敗: ${message}`;
        }
      }
      
      alert(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, invitation: null });
  };

  // 計算婚禮倒數天數
  const calculateDaysUntilWedding = () => {
    if (!weddingDate) return 78; // 預設值
    
    const today = new Date();
    const wedding = new Date(weddingDate);
    const timeDiff = wedding.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff > 0 ? daysDiff : 0;
  };

  // 使用真實數據或預設值
  const displayStats = rsvpStats || {
    total: 0,
    attending: 0,
    declined: 0,
    totalGuests: 0
  };

  // const responseRate = displayStats.total > 0 
  //   ? Math.round((displayStats.attending + displayStats.declined) / displayStats.total * 100)
  //   : 0;

  // 核心功能模組 - 對應 Sidebar 的四個主要功能
  const coreModules = [{
    id: 'rsvp',
    name: '賓客管理',
    icon: <MessageSquare className="h-6 w-6" />,
    description: '管理邀請、追蹤回覆',
    progress: displayStats.total > 0 ? Math.round((displayStats.attending + displayStats.declined) / displayStats.total * 100) : 0
  }, {
    id: 'tables',
    name: '座位安排',
    icon: <Users className="h-6 w-6" />,
    description: '安排賓客座位',
    progress: 0 // 這裡之後可以接入真實的座位安排進度
  }, {
    id: 'gifts',
    name: '紅包管理',
    icon: <Gift className="h-6 w-6" />,
    description: '記錄禮金收入',
    progress: 0 // 這裡之後可以接入真實的紅包記錄進度
  }, {
    id: 'timeline',
    name: '時間規劃',
    icon: <Calendar className="h-6 w-6" />,
    description: '婚禮任務規劃',
    progress: 0 // 這裡之後可以接入真實的任務完成進度
  }];
  // 功能導航指南 - 調整為實際功能流程
  const functionalGuide = [{
    id: 1,
    title: '建立邀請函',
    description: '選擇喜歡的模板，填入你們的甜蜜資訊 💕',
    action: () => onQuickInvite?.(),
    stepNumber: 1
  }, {
    id: 2,
    title: '管理賓客回覆',
    description: '可以檢視親友並調整為男女方親友 👨‍👩',
    action: () => setActiveModule('rsvp'),
    stepNumber: 2
  }, {
    id: 3,
    title: '安排座位',
    description: '不妨試試看我們超方便的賓客拖拉功能！🪑✨',
    action: () => setActiveModule('tables'),
    stepNumber: 3
  }, {
    id: 4,
    title: '追蹤禮金',
    description: '輕鬆記錄每一份祝福的紅包 🧧💰',
    action: () => setActiveModule('gifts'),
    stepNumber: 4
  }];

  // 檢查是否有邀請函
  const hasInvitations = !loadingInvitations && weddingInvitations.length > 0;

  // 功能導航指南完成狀態
  // 步驟 1/2 使用 Dashboard 已載入的資料判斷；步驟 3/4（座位/紅包）
  // 目前沒有便宜的資料源，維持「未知＝未完成」，避免額外查詢
  const guideCompletion: Record<number, boolean> = {
    1: hasInvitations,
    2: displayStats.total > 0,
    3: false,
    4: false,
  };
  const completedGuideCount = Object.values(guideCompletion).filter(Boolean).length;
  const firstIncompleteStep = functionalGuide.find(guide => !guideCompletion[guide.stepNumber])?.stepNumber;

  log.debug('渲染狀態:', {
    loadingInvitations,
    invitationCount: weddingInvitations.length,
    hasInvitations,
    shouldShowWelcome: !loadingInvitations && !hasInvitations
  });
  
  return <div className="space-y-10">
      {/* Hero Section - 條件式內容 */}
      <section className="bg-gradient-to-br from-wedding-pink/10 to-wedding-pink/5 rounded-xl p-8 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-center mb-4">
            <div className="bg-wedding-pink/10 p-4 rounded-full">
              <Heart className="h-8 w-8 text-wedding-pink" />
            </div>
          </div>
          
          {loadingInvitations ? (
            // 載入中的內容
            <>
              <h1 className="text-3xl font-bold text-wedding-text-title mb-4">
                載入中...
              </h1>
              <p className="text-lg text-wedding-text-body">
                正在載入您的邀請函資料
              </p>
            </>
          ) : !hasInvitations ? (
            // 無邀請函時的內容
            <>
              <h1 className="text-3xl font-bold text-wedding-text-title mb-4">
                3分鐘建立你的婚禮邀請函
              </h1>
              <p className="text-lg text-wedding-text-body mb-8">
                簡單三步驟：選擇模板 → 填入資訊 → 分享邀請連結，讓賓客即時回覆
              </p>
              <div className="flex justify-center">
                <PrimaryButton 
                  onClick={() => onQuickInvite?.()}
                  size="lg"
                  className="shadow-lg flex items-center"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  立即建立邀請函
                  <ArrowRight className="h-5 w-5 ml-2" />
                </PrimaryButton>
              </div>
            </>
          ) : (
            // 有邀請函時的內容
            <>
              <h1 className="text-3xl font-bold text-wedding-text-title mb-4">
                婚禮籌備進行中
              </h1>
              <p className="text-lg text-wedding-text-body mb-6">
                距離大日子還有 <span className="font-semibold text-wedding-pink">{calculateDaysUntilWedding()}</span> 天
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-lg mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-wedding-text-title">{displayStats.total}</div>
                  <div className="text-sm text-wedding-text-body">邀請賓客</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-wedding-text-title">{displayStats.attending + displayStats.declined}</div>
                  <div className="text-sm text-wedding-text-body">已回覆</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-wedding-text-title">{displayStats.totalGuests}</div>
                  <div className="text-sm text-wedding-text-body">確認出席</div>
                </div>
              </div>
              
              {displayStats.total > 0 && displayStats.attending + displayStats.declined < displayStats.total && (
                <div className="mt-6">
                  <PrimaryButton 
                    onClick={() => setActiveModule('rsvp')}
                    className="shadow-lg flex items-center"
                  >
                    <MessageSquare className="h-5 w-5 mr-2" />
                    查看待回覆賓客
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </PrimaryButton>
                </div>
              )}
            </>
          )}
        </div>
      </section>


      {/* My Invitations Section - 只在有邀請函時顯示 */}
      {hasInvitations && (
      <ThemedCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-wedding-text-title">我的邀請函</h2>
          <span className="text-sm text-wedding-text-body">{weddingInvitations.length} 個邀請函</span>
        </div>
        {loadingInvitations ? (
          <div className="text-center py-8 text-wedding-text-body">
            <p>載入邀請函中...</p>
          </div>
        ) : weddingInvitations.length === 0 ? (
          <div className="text-center py-8 text-wedding-text-body">
            <p>尚未建立任何邀請函</p>
            <div className="mt-4 space-x-4">
              <PrimaryButton 
                onClick={() => onQuickInvite?.()}
                className="transition-colors"
              >
                立即建立你的第一個邀請函
              </PrimaryButton>
              {onRetryLoad && (
                <PrimaryButton 
                  onClick={onRetryLoad}
                  className="bg-gray-500 hover:bg-gray-600 text-white transition-colors"
                >
                  重新載入
                </PrimaryButton>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {weddingInvitations.map((invitation) => (
              <ThemedCard key={invitation.id} className="p-4 hover:shadow-xl transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-wedding-text-title">{invitation.brideName} & {invitation.groomName}</h3>
                      {invitation.role && (
                        <CollaborationRoleBadge role={invitation.role} size="small" />
                      )}
                    </div>
                    <p className="text-sm text-wedding-text-body">{invitation.weddingDate}</p>
                  </div>
                  <div className="flex space-x-2">
                    {/* 編輯按鈕 - 所有用戶都可編輯 */}
                    <button
                      onClick={() => onEditInvitation?.(invitation.inviteCode)}
                      className="p-2 text-wedding-text-body hover:text-wedding-pink hover:bg-wedding-pink/10 rounded-lg transition-colors"
                      title="編輯邀請函"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {/* 刪除按鈕 - 只有擁有者可以刪除 */}
                    {invitation.role !== 'collaborator' && (
                      <button
                        onClick={() => handleDeleteClick(invitation)}
                        className="p-2 text-wedding-text-body hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="刪除邀請函"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-sm text-wedding-text-body mb-3">
                  <p className="truncate">{invitation.venue}</p>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex space-x-2">
                    <span className="text-xs px-2 py-1 bg-wedding-pink/20 text-wedding-pink rounded-full">
                      已建立
                    </span>
                    {invitation.acceptSubmissions === false ? (
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full flex items-center">
                        <X className="h-3 w-3 mr-1" />
                        RSVP 已關閉
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        RSVP 開放中
                      </span>
                    )}
                  </div>
                  <a
                    href={`/invite/${invitation.inviteCode}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-wedding-pink hover:text-wedding-pink/80 underline"
                  >
                    預覽邀請函
                  </a>
                </div>
              </ThemedCard>
            ))}
          </div>
        )}
      </ThemedCard>
      )}

      {/* Quick Access Modules */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <ThemedCard className="lg:col-span-3 p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-lg font-semibold text-wedding-text-title">
              {hasInvitations ? '核心功能總覽' : '婚禮規劃功能'}
            </h2>
            <span className="text-sm text-wedding-text-body">
              {hasInvitations ? '快速進入各功能模組' : '了解平台核心功能'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {coreModules.map(module => (
              <ThemedCard 
                key={module.id} 
                onClick={() => setActiveModule(module.id)} 
                className="p-6 hover:border-wedding-pink/50 hover:bg-wedding-pink/5 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="p-2 bg-wedding-pink/10 rounded-md mr-3">
                      {module.icon}
                    </div>
                    <h3 className="font-medium text-wedding-text-title">{module.name}</h3>
                  </div>
                  {hasInvitations && (
                    <span className="text-sm text-wedding-text-body">
                      {module.progress}%
                    </span>
                  )}
                </div>
                <p className="text-sm text-wedding-text-body mb-3">{module.description}</p>
                {hasInvitations && (
                  <div className="w-full bg-gradient-to-r from-gray-200 to-gray-300 rounded-full h-3 shadow-inner">
                    <div 
                      className="bg-gradient-to-r from-wedding-pink to-wedding-gold h-3 rounded-full transition-all duration-500 ease-out shadow-sm" 
                      style={{
                        width: `${module.progress}%`
                      }}
                    ></div>
                  </div>
                )}
              </ThemedCard>
            ))}
          </div>
        </ThemedCard>
        
        <ThemedCard className="lg:col-span-2 p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-wedding-text-title">
              {hasInvitations ? '下一步做什麼？' : '功能導航指南'}
            </h2>
            <span className="text-sm text-wedding-text-body">{completedGuideCount} / 4</span>
          </div>
          <div className="space-y-3">
            {functionalGuide.map(guide => {
              const isCompleted = guideCompletion[guide.stepNumber];
              const isCurrent = guide.stepNumber === firstIncompleteStep;
              return (
                <ThemedCard
                  key={guide.id}
                  className={`p-4 cursor-pointer hover:bg-wedding-pink/5 hover:border-wedding-pink/50 transition-all duration-200 ${
                    isCurrent ? 'border-wedding-pink/40 bg-wedding-pink/8' : ''
                  } ${isCompleted ? 'opacity-60' : ''}`}
                  onClick={guide.action}
                >
                  <div className="flex items-start space-x-3">
                    <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white text-xs font-medium ${
                      isCompleted ? 'bg-green-500' : isCurrent ? 'bg-wedding-pink shadow-sm' : 'bg-wedding-text-body/50'
                    }`}>
                      {isCompleted ? <Check className="h-3.5 w-3.5" /> : guide.stepNumber}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium text-sm mb-1 text-wedding-text-title ${isCompleted ? 'line-through' : ''}`}>
                        {guide.title}
                      </h3>
                      <p className="text-xs text-wedding-text-body leading-relaxed">{guide.description}</p>
                    </div>
                  </div>
                </ThemedCard>
              );
            })}
          </div>
          
          {hasInvitations && (
            <div className="mt-6 pt-4 border-t border-wedding-pink/20">
              <h3 className="font-medium text-sm mb-3 text-wedding-text-title">快速提醒</h3>
              <div className="space-y-2">
                <div className="flex items-center text-xs">
                  <Clock className="h-4 w-4 text-wedding-gold mr-2" />
                  <span className="text-wedding-text-body">
                    距離婚禮還有 {calculateDaysUntilWedding()} 天
                  </span>
                </div>
                {displayStats.total > 0 && displayStats.attending + displayStats.declined < displayStats.total && (
                  <div className="flex items-center text-xs">
                    <MessageSquare className="h-4 w-4 text-wedding-pink mr-2" />
                    <span className="text-wedding-text-body">
                      還有 {displayStats.total - displayStats.attending - displayStats.declined} 位賓客尚未回覆
                    </span>
                  </div>
                )}
                <div className="flex items-center text-xs">
                  <CheckCircle className="h-4 w-4 text-wedding-pink mr-2" />
                  <span className="text-wedding-text-body">
                    目前已確認 {displayStats.totalGuests} 位賓客出席
                  </span>
                </div>
              </div>
            </div>
          )}
        </ThemedCard>
      </section>

      {/* 刪除確認對話框 */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <ThemedCard className="p-6 max-w-md w-mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-wedding-text-title">確認刪除邀請函</h3>
              <button
                onClick={handleDeleteCancel}
                className="text-wedding-text-body hover:text-wedding-pink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-wedding-text-body mb-2">
                您確定要刪除以下邀請函嗎？
              </p>
              {deleteConfirm.invitation && (
                <div className="bg-wedding-bg rounded-lg p-3">
                  <h4 className="font-medium text-wedding-text-title">
                    {deleteConfirm.invitation.brideName} & {deleteConfirm.invitation.groomName}
                  </h4>
                  <p className="text-sm text-wedding-text-body">
                    {deleteConfirm.invitation.weddingDate} • {deleteConfirm.invitation.venue}
                  </p>
                </div>
              )}
              <p className="text-sm text-red-600 mt-3">
                ⚠️ 此操作無法復原，且會影響已發送的邀請連結
              </p>
            </div>

            <div className="flex space-x-3">
              <PrimaryButton
                onClick={handleDeleteCancel}
                variant="outline"
                className="flex-1 border-wedding-pink/30 text-wedding-text-body hover:bg-wedding-pink/10 hover:text-wedding-pink"
                disabled={isDeleting}
              >
                取消
              </PrimaryButton>
              <PrimaryButton
                onClick={handleDeleteConfirm}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-300"
                disabled={isDeleting}
              >
                {isDeleting ? '刪除中...' : '確認刪除'}
              </PrimaryButton>
            </div>
          </ThemedCard>
        </div>
      )}
    </div>;
};